import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

/** WhatsApp-style 1:1 voice-call hook using WebRTC + WS signalling.
 *
 *  States (`phase`):
 *    idle | calling | ringing | in-call
 *
 *  Signalling protocol (event names go through the existing chat WS):
 *    call.invite  { conversation_id, sdp }        caller → callee
 *    call.accept  { conversation_id, sdp }        callee → caller
 *    call.decline { conversation_id }             callee → caller
 *    call.cancel  { conversation_id }             caller → callee (before answer)
 *    call.hangup  { conversation_id }             either party during call
 *    call.ice     { conversation_id, candidate }  either party
 */
const RTC_CONFIG = {
  iceServers: [
    { urls: "stun:stun.l.google.com:19302" },
    { urls: "stun:stun1.l.google.com:19302" },
  ],
};

export default function useVoiceCall({ send, activeConversation, currentUserId, incomingEvent }) {
  const [phase, setPhase] = useState("idle");     // idle | calling | ringing | in-call
  const [peerId, setPeerId] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [muted, setMuted] = useState(false);
  const [startedAt, setStartedAt] = useState(null);

  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const pendingIceRef = useRef([]);
  const ringtoneRef = useRef(null);
  const outgoingToneRef = useRef(null);

  // --- Cleanup ---
  const cleanup = useCallback(() => {
    try { pcRef.current?.close(); } catch { /* */ }
    pcRef.current = null;
    try { localStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* */ }
    localStreamRef.current = null;
    pendingIceRef.current = [];
    if (remoteAudioRef.current) { remoteAudioRef.current.srcObject = null; }
    stopTones();
    setMuted(false);
    setStartedAt(null);
  }, []);

  const endCall = useCallback((outcome = "completed") => {
    const cid = conversationId;
    const start = startedAt;
    if (cid) {
      try {
        send({ event: "call.hangup", conversation_id: cid });
      } catch { /* */ }
      // Only the initiator (or whoever ends after connect) logs the call.
      const duration = start ? Math.max(0, Math.round((Date.now() - start) / 1000)) : 0;
      api.post("/chat/calls/log", {
        conversation_id: cid,
        kind: "voice",
        outcome: outcome === "completed" && duration === 0 ? "cancelled" : outcome,
        duration_seconds: duration,
      }).catch(() => {});
    }
    cleanup();
    setPhase("idle");
    setPeerId(null);
    setConversationId(null);
  }, [conversationId, startedAt, send, cleanup]);

  // --- Tones ---
  // Loud, telephone-style ringtone for INCOMING calls. Plays the bundled
  // MP3 (`/sounds/ringtone.mp3`) on loop at max volume; falls back to a
  // synthesized double-brrring if the file fails or autoplay is blocked.
  // Also triggers device vibration so mobile users notice even on silent.
  const playRingtone = () => {
    // Kick off vibration regardless of audio source.
    let vibInterval = null;
    try {
      const vibrate = () => { try { navigator.vibrate?.([600, 200, 600, 3000]); } catch { /* */ } };
      vibrate();
      vibInterval = setInterval(vibrate, 7200);
    } catch { /* */ }

    try {
      const audio = new Audio("/sounds/ringtone.mp3");
      audio.loop = true;
      audio.volume = 1.0;
      audio.preload = "auto";
      const p = audio.play();
      if (p && typeof p.catch === "function") {
        p.catch(() => playFallbackRingtone(vibInterval));
      }
      ringtoneRef.current = { audio, vibInterval };
      return;
    } catch { /* fall through */ }

    playFallbackRingtone(vibInterval);
  };

  // Synthesized fallback ringtone (two-tone bell, telephone double-brrring).
  const playFallbackRingtone = (vibInterval) => {
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      if (ctx.state === "suspended") { try { ctx.resume(); } catch { /* */ } }

      const master = ctx.createGain();
      master.gain.value = 3.2;
      const comp = ctx.createDynamicsCompressor();
      comp.threshold.value = -12; comp.knee.value = 8; comp.ratio.value = 6;
      comp.attack.value = 0.003; comp.release.value = 0.15;
      master.connect(comp).connect(ctx.destination);

      const oscA = ctx.createOscillator();
      const oscB = ctx.createOscillator();
      oscA.type = "sine";  oscA.frequency.value = 480;
      oscB.type = "sine";  oscB.frequency.value = 620;
      const env = ctx.createGain();
      env.gain.value = 0;
      oscA.connect(env); oscB.connect(env);
      env.connect(master);
      oscA.start(); oscB.start();

      const scheduleRing = () => {
        const t0 = ctx.currentTime;
        [{ on: 0.00, off: 2.00 }, { on: 2.40, off: 4.40 }].forEach(({ on, off }) => {
          env.gain.setValueAtTime(0.0001, t0 + on);
          env.gain.exponentialRampToValueAtTime(1.0, t0 + on + 0.02);
          env.gain.setValueAtTime(1.0, t0 + off - 0.05);
          env.gain.exponentialRampToValueAtTime(0.0001, t0 + off);
        });
      };
      scheduleRing();
      const interval = setInterval(scheduleRing, 7200);
      ringtoneRef.current = { ctx, osc: oscA, osc2: oscB, interval, vibInterval };
    } catch { /* */ }
  };
  const playOutgoingTone = () => {
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine"; osc.frequency.value = 420;
      gain.gain.value = 0;
      osc.connect(gain).connect(ctx.destination);
      osc.start();
      const bumpUp = () => { gain.gain.setValueAtTime(0.08, ctx.currentTime); gain.gain.setValueAtTime(0, ctx.currentTime + 0.3); };
      bumpUp();
      const interval = setInterval(bumpUp, 3000);
      outgoingToneRef.current = { ctx, osc, interval };
    } catch { /* */ }
  };
  const stopTones = () => {
    for (const ref of [ringtoneRef, outgoingToneRef]) {
      const cur = ref.current;
      if (cur) {
        try { clearInterval(cur.interval); } catch { /* */ }
        try { clearInterval(cur.vibInterval); } catch { /* */ }
        try { navigator.vibrate?.(0); } catch { /* */ }
        try { cur.audio?.pause(); if (cur.audio) cur.audio.currentTime = 0; } catch { /* */ }
        try { cur.osc?.stop(); } catch { /* */ }
        try { cur.osc2?.stop(); } catch { /* */ }
        try { cur.ctx?.close(); } catch { /* */ }
        ref.current = null;
      }
    }
  };

  // --- Peer connection helpers ---
  const buildPeer = useCallback((cid) => {
    const pc = new RTCPeerConnection(RTC_CONFIG);
    pc.onicecandidate = (e) => {
      if (e.candidate) send({ event: "call.ice", conversation_id: cid, candidate: e.candidate });
    };
    pc.ontrack = (e) => {
      if (remoteAudioRef.current && e.streams[0]) {
        remoteAudioRef.current.srcObject = e.streams[0];
        remoteAudioRef.current.play?.().catch(() => {});
      }
    };
    pc.onconnectionstatechange = () => {
      if (["failed", "closed", "disconnected"].includes(pc.connectionState)) {
        endCall("completed");
      } else if (pc.connectionState === "connected") {
        stopTones();
        setStartedAt((prev) => prev || Date.now());
        setPhase("in-call");
      }
    };
    return pc;
  }, [send, endCall]);

  const acquireMic = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    localStreamRef.current = stream;
    return stream;
  };

  // --- Public actions ---
  const startCall = useCallback(async (conv, targetUserId) => {
    if (!conv?.id || !targetUserId) return;
    try {
      setConversationId(conv.id);
      setPeerId(targetUserId);
      setPhase("calling");
      const pc = buildPeer(conv.id);
      pcRef.current = pc;
      const stream = await acquireMic();
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      send({ event: "call.invite", conversation_id: conv.id, sdp: pc.localDescription });
      playOutgoingTone();
    } catch (e) {
      cleanup();
      setPhase("idle");
      setPeerId(null);
      setConversationId(null);
      throw e;
    }
  }, [buildPeer, send, cleanup]);

  const acceptCall = useCallback(async () => {
    // We already have an incoming offer stored on window.__pending_offer
    const pending = window.__pending_offer;
    if (!pending) return;
    delete window.__pending_offer;
    const { conversation_id: cid, sdp, from_user_id } = pending;
    try {
      stopTones();
      const pc = buildPeer(cid);
      pcRef.current = pc;
      const stream = await acquireMic();
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));
      await pc.setRemoteDescription(new RTCSessionDescription(sdp));
      // Drain queued ICE from before we had the remote description
      for (const c of pendingIceRef.current) {
        try { await pc.addIceCandidate(new RTCIceCandidate(c)); } catch { /* */ }
      }
      pendingIceRef.current = [];
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      send({ event: "call.accept", conversation_id: cid, sdp: pc.localDescription });
      setConversationId(cid);
      setPeerId(from_user_id);
      setPhase("in-call");
      setStartedAt(Date.now());
    } catch (e) {
      cleanup();
      setPhase("idle");
      throw e;
    }
  }, [buildPeer, send, cleanup]);

  const declineCall = useCallback(() => {
    const pending = window.__pending_offer;
    if (pending) {
      send({ event: "call.decline", conversation_id: pending.conversation_id });
      // Log missed on callee side
      api.post("/chat/calls/log", {
        conversation_id: pending.conversation_id,
        kind: "voice", outcome: "declined", duration_seconds: 0,
      }).catch(() => {});
      delete window.__pending_offer;
    }
    cleanup();
    stopTones();
    setPhase("idle");
    setPeerId(null);
    setConversationId(null);
  }, [send, cleanup]);

  const toggleMute = useCallback(() => {
    const stream = localStreamRef.current;
    if (!stream) return;
    const track = stream.getAudioTracks()[0];
    if (!track) return;
    track.enabled = !track.enabled;
    setMuted(!track.enabled);
  }, []);

  // --- Incoming event dispatcher ---
  useEffect(() => {
    if (!incomingEvent) return;
    const ev = incomingEvent;
    const cid = ev.conversation_id;
    const from = ev.from_user_id;
    if (ev.event === "call.invite") {
      // Reject if already busy
      if (phase !== "idle") {
        send({ event: "call.decline", conversation_id: cid });
        return;
      }
      window.__pending_offer = { conversation_id: cid, sdp: ev.sdp, from_user_id: from };
      setPeerId(from);
      setConversationId(cid);
      setPhase("ringing");
      playRingtone();
    } else if (ev.event === "call.accept") {
      if (!pcRef.current) return;
      pcRef.current.setRemoteDescription(new RTCSessionDescription(ev.sdp)).then(async () => {
        for (const c of pendingIceRef.current) {
          try { await pcRef.current.addIceCandidate(new RTCIceCandidate(c)); } catch { /* */ }
        }
        pendingIceRef.current = [];
      }).catch(() => {});
    } else if (ev.event === "call.decline") {
      // Caller side — target rejected
      api.post("/chat/calls/log", {
        conversation_id: cid, kind: "voice", outcome: "declined", duration_seconds: 0,
      }).catch(() => {});
      cleanup();
      setPhase("idle");
      setPeerId(null);
      setConversationId(null);
    } else if (ev.event === "call.cancel") {
      // Callee side — caller hung up before we answered
      api.post("/chat/calls/log", {
        conversation_id: cid, kind: "voice", outcome: "missed", duration_seconds: 0,
      }).catch(() => {});
      delete window.__pending_offer;
      cleanup();
      setPhase("idle");
      setPeerId(null);
      setConversationId(null);
    } else if (ev.event === "call.hangup") {
      // Peer ended a live call — don't double-log; peer's endCall already logged.
      cleanup();
      setPhase("idle");
      setPeerId(null);
      setConversationId(null);
    } else if (ev.event === "call.ice" && ev.candidate) {
      if (pcRef.current && pcRef.current.remoteDescription) {
        pcRef.current.addIceCandidate(new RTCIceCandidate(ev.candidate)).catch(() => {});
      } else {
        pendingIceRef.current.push(ev.candidate);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomingEvent]);

  // Cancel outgoing while calling
  const cancelOutgoing = useCallback(() => {
    if (conversationId) send({ event: "call.cancel", conversation_id: conversationId });
    if (conversationId) {
      api.post("/chat/calls/log", {
        conversation_id: conversationId, kind: "voice", outcome: "cancelled", duration_seconds: 0,
      }).catch(() => {});
    }
    cleanup();
    setPhase("idle");
    setPeerId(null);
    setConversationId(null);
  }, [conversationId, send, cleanup]);

  // Cleanup on unmount
  useEffect(() => () => cleanup(), [cleanup]);

  return {
    phase, peerId, conversationId, muted, startedAt,
    remoteAudioRef,
    startCall, acceptCall, declineCall, endCall, toggleMute, cancelOutgoing,
  };
}
